/**
 * Edit User Drawer
 * Form for updating existing users
 */

import { useEffect } from 'react'
import { useTranslation, useForm, Controller, zodResolver, z, validateSaudiPhone, normalizeSaudiPhoneForStorage } from '@ufc/core'
import { isSystemUserRole } from '@ufc/domains/auth'
import {
  BaseDrawer,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  Edit,
  Save,
  X,
} from '@ufc/uikit'
import type { StrapiRoleType, UpdateUserRequest, User } from '../../domain/types/user.types'
import {
  USER_FORM_FIELDS,
  USER_FORM_T_KEYS,
  roleRequiresFactory,
  roleAllowsLocationOption,
  INVITABLE_ROLE_OPTIONS,
  ALL_PLANTS_VALUE,
  type InvitableRole,
} from '../../domain/utils/user-form-schema'
import { getCombinedAssignmentValue, parseCombinedAssignmentValue, type InviteFactoryOption } from './invite-user-drawer'

type EditableRoleType = InvitableRole

const editableUserFormSchema = z
  .object({
    name: z.string().min(1, USER_FORM_T_KEYS.VALIDATION.NAME_REQUIRED).max(255),
    email: z
      .string()
      .min(1, USER_FORM_T_KEYS.VALIDATION.EMAIL_REQUIRED)
      .email(USER_FORM_T_KEYS.VALIDATION.EMAIL_INVALID),
    phone: z.string().optional().refine((v) => !v || v.trim() === '' || validateSaudiPhone(v).valid, 'validation.phoneSaudiFormat'),
    role: z.enum(INVITABLE_ROLE_OPTIONS.map((option) => option.value) as any, {}).optional(),
    factoryId: z.string().optional(),
    locationId: z.string().optional(),
  })

type EditableUserFormData = z.infer<typeof editableUserFormSchema>

export interface EditUserDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: User | null
  onSubmit: (data: UpdateUserRequest) => void
  factoryOptions?: InviteFactoryOption[]
  locationOptions?: InviteFactoryOption[]
  isLoading?: boolean
  isRTL?: boolean
}

function formatFactoryLabel(factory: InviteFactoryOption): string {
  if (!factory.code) return factory.name
  return `${factory.name} (${factory.code})`
}

function getRoleDisplayName(roleType: string | undefined, t: ReturnType<typeof useTranslation>['t']): string {
  const roleNames: Record<string, string> = {
    super_admin: t('admin.roles.superAdmin', 'Super Admin'),
    admin: t('admin.roles.admin', 'Admin'),
    finance_admin: t('admin.roles.financeAdmin', 'Finance Admin'),
    tenant_admin: t('admin.roles.tenantAdmin', 'Tenant Admin'),
    tenant_user: t('admin.roles.tenantUser', 'Tenant User'),
    sales_office_user: t('admin.roles.salesOfficeUser', 'Sales Office User'),
    security_guard: t('admin.roles.securityGuard', 'Security Guard'),
    safety_manager: t('admin.roles.safetyManager', 'Safety Manager'),
    services_manager: t('admin.roles.servicesManager', 'Services Manager'),
    kiosk_user: t('admin.roles.kiosk', 'Kiosk'),
    employee: t('admin.roles.employee', 'Employee'),
    customer: t('admin.roles.customer', 'Customer'),
    customer_operator: t('admin.roles.customerOperator', 'Customer Operator'),
    scale_operator: t('admin.roles.scaleOperator', 'Scale Operator'),
    scale_admin: t('admin.roles.scaleAdmin', 'Scale Admin'),
    authenticated: t('admin.roles.authenticated', 'Authenticated'),
    public: t('admin.roles.public', 'Public'),
    ecommerce: t('admin.roles.ecommerce', 'E-commerce'),
    manager: t('admin.roles.manager', 'Manager'),
    operator: t('admin.roles.operator', 'Operator'),
    viewer: t('admin.roles.viewer', 'Viewer'),
  }

  if (!roleType) return ''
  return roleNames[roleType] ?? roleType
}

function getResolvedRoleType(user: User | null): EditableRoleType | undefined {
  if (!user) return undefined

  const roleType = user.strapiRole?.type ?? user.role
  if (INVITABLE_ROLE_OPTIONS.some((option) => option.value === roleType)) {
    return roleType as EditableRoleType
  }

  if (isSystemUserRole(roleType)) {
    // Keep system users editable with the restricted 4-role list.
    return 'sales_office_user'
  }

  return undefined
}

function canEditUserRole(_user: User | null): boolean {
  // Role is always editable — no role-based restriction.
  return true
}

function getDefaultEditValues(user: User | null): EditableUserFormData {
  return {
    name: user?.name ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    role: getResolvedRoleType(user),
    // Only one of factory/location is ever set on a user. Leave the other
    // empty (not ALL_PLANTS_VALUE) so getCombinedAssignmentValue picks the
    // right prefix — defaulting both to ALL_PLANTS_VALUE made every truthy
    // placeholder look like a real factory selection and blanked the Select.
    factoryId: user?.factoryId ?? '',
    locationId: user?.locationId ?? '',
  }
}

export function EditUserDrawer({
  open,
  onOpenChange,
  user,
  onSubmit,
  factoryOptions = [],
  locationOptions = [],
  isLoading = false,
  isRTL = false,
}: EditUserDrawerProps) {
  const { t } = useTranslation()
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<EditableUserFormData>({
    resolver: zodResolver(editableUserFormSchema),
    defaultValues: getDefaultEditValues(user),
  })
  const selectedRole = watch('role')
  const canEditRole = canEditUserRole(user)
  const currentRoleType = user?.strapiRole?.type ?? user?.role
  const requiresFactory = roleRequiresFactory(selectedRole as StrapiRoleType | undefined)
  const showLocationOptions = roleAllowsLocationOption(selectedRole as StrapiRoleType | undefined)

  useEffect(() => {
    if (open) {
      reset(getDefaultEditValues(user))
    }
  }, [open, reset, user])

  const onFormSubmit = (data: EditableUserFormData) => {
    if (!user) return

    const phoneTrimmed = data.phone?.trim()
    const phoneNormalized = phoneTrimmed ? (normalizeSaudiPhoneForStorage(phoneTrimmed) ?? phoneTrimmed) : undefined
    const roleType = data.role as StrapiRoleType | undefined
    const factoryIdValue =
      data.factoryId && data.factoryId !== ALL_PLANTS_VALUE
        ? data.factoryId.trim()
        : null
    const locationIdValue =
      data.locationId && data.locationId !== ALL_PLANTS_VALUE
        ? data.locationId.trim()
        : null
    const updateRequest: UpdateUserRequest = {
      userId: user.id,
      data: {
        name: data.name.trim(),
        phone: phoneNormalized,
        ...(canEditRole
          && roleType
          ? {
              strapiRoleType: roleType,
              factoryId: factoryIdValue,
              locationId: locationIdValue,
            }
          : {}),
      },
    }

    onSubmit(updateRequest)
  }

  const handleSave = handleSubmit(onFormSubmit)
  const headerGradient = 'from-blue-600 to-blue-700'

  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
        <Edit className="w-6 h-6 text-white" />
      </div>
      <div className={cn(isRTL && 'text-right')}>
        <h2 className="text-lg font-bold text-white">{t('admin.editUser', 'Edit User')}</h2>
        <p className="text-sm text-white/80">
          {t('admin.user.form.editDescription', 'Update user profile and access settings')}
        </p>
      </div>
    </div>
  )

  const footerContent = (
    <div className="flex gap-3">
      <Button
        type="button"
        variant="outline"
        className="flex-1 h-12 rounded-xl"
        onClick={() => onOpenChange(false)}
        disabled={isLoading}
      >
        <X className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
        {t('common.cancel', 'Cancel')}
      </Button>
      <Button type="button" className="flex-1 h-12 rounded-xl" disabled={isLoading} onClick={handleSave}>
        <Save className={cn('w-4 h-4', isRTL ? 'ms-2' : 'me-2')} />
        {isLoading ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')}
      </Button>
    </div>
  )

  return (
    <BaseDrawer
      open={open}
      onOpenChange={onOpenChange}
      isRTL={isRTL}
      headerGradient={headerGradient}
      headerContent={headerContent}
      footerContent={footerContent}
      fixedWidth={480}
      title={t('admin.editUser', 'Edit User')}
    >
      <form id="edit-user-form" onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('admin.user.form.userInfo', 'User Information')}
          </h3>

          <div className="space-y-2">
            <Label htmlFor={USER_FORM_FIELDS.NAME}>
              {t(USER_FORM_T_KEYS.LABELS.NAME, 'Full Name')} *
            </Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id={USER_FORM_FIELDS.NAME}
                    value={value ?? ''}
                    placeholder={t(USER_FORM_T_KEYS.PLACEHOLDERS.NAME, 'Enter full name')}
                    className={cn('h-11 rounded-xl', errors.name && 'border-destructive')}
                    {...fieldProps}
                  />
                )
              }}
            />
            {errors.name && <p className="text-xs text-destructive">{t(errors.name.message || '')}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor={USER_FORM_FIELDS.EMAIL}>
              {t(USER_FORM_T_KEYS.LABELS.EMAIL, 'Email Address')}
            </Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  id={USER_FORM_FIELDS.EMAIL}
                  type="email"
                  value={field.value ?? ''}
                  className="h-11 rounded-xl bg-muted/40"
                  disabled
                  dir="ltr"
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={USER_FORM_FIELDS.PHONE}>
              {t(USER_FORM_T_KEYS.LABELS.PHONE, 'Phone Number')}
            </Label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => {
                const { ref: _ref, value, ...fieldProps } = field
                return (
                  <Input
                    id={USER_FORM_FIELDS.PHONE}
                    type="tel"
                    value={value ?? ''}
                    placeholder={t(USER_FORM_T_KEYS.PLACEHOLDERS.PHONE, '+966 5XX XXX XXXX')}
                    className={cn('h-11 rounded-xl', errors.phone && 'border-destructive')}
                    dir="ltr"
                    {...fieldProps}
                  />
                )
              }}
            />
            {errors.phone && (
              <p className="text-xs text-destructive mt-1">
                {t((errors.phone.message as string) ?? 'validation.phoneSaudiFormat')}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t('admin.user.form.roleAssignment', 'Role & Assignment')}
          </h3>

          <div className="space-y-2">
            <Label>{t(USER_FORM_T_KEYS.LABELS.ROLE, 'Role')}{canEditRole ? ' *' : ''}</Label>
            {canEditRole ? (
              <>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value ?? ''} onValueChange={field.onChange}>
                      <SelectTrigger className={cn('h-11 rounded-xl', errors.role && 'border-destructive')}>
                        <SelectValue placeholder={t('admin.user.form.selectRole', 'Select role')} />
                      </SelectTrigger>
                      <SelectContent>
                        {INVITABLE_ROLE_OPTIONS.map((roleOption) => (
                          <SelectItem key={roleOption.value} value={roleOption.value}>
                            {t(roleOption.labelKey, roleOption.label)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.role && (
                  <p className="text-xs text-destructive">{t((errors.role.message as string) || '')}</p>
                )}
              </>
            ) : (
              <Input
                value={getRoleDisplayName(currentRoleType, t)}
                className="h-11 rounded-xl bg-muted/40"
                disabled
              />
            )}
          </div>

          {canEditRole && (
            <div className="space-y-2">
              <Label>
                {t(USER_FORM_T_KEYS.LABELS.FACTORY, 'Plant / Location')}
                {requiresFactory && ' *'}
              </Label>
              <Select
                value={getCombinedAssignmentValue(watch('factoryId'), watch('locationId'))}
                onValueChange={(value) => {
                  const { factoryId, locationId } = parseCombinedAssignmentValue(value)
                  setValue('factoryId', factoryId)
                  setValue('locationId', locationId)
                  trigger(['factoryId', 'locationId'])
                }}
              >
                <SelectTrigger className={cn('h-11 rounded-xl', errors.factoryId && 'border-destructive')}>
                  <SelectValue
                    placeholder={t(USER_FORM_T_KEYS.PLACEHOLDERS.FACTORY, 'All Plants')}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_PLANTS_VALUE}>
                    {t(USER_FORM_T_KEYS.LABELS.ALL_PLANTS, 'All Plants')}
                  </SelectItem>
                  {factoryOptions.map((factory) => (
                    <SelectItem key={`factory:${factory.id}`} value={`factory:${factory.id}`}>
                      {formatFactoryLabel(factory)}
                    </SelectItem>
                  ))}
                  {showLocationOptions && locationOptions.map((location) => (
                    <SelectItem key={`location:${location.id}`} value={`location:${location.id}`}>
                      {formatFactoryLabel(location)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.factoryId && (
                <p className="text-xs text-destructive">
                  {t((errors.factoryId.message as string) || '', 'Plant is required for this role')}
                </p>
              )}
            </div>
          )}
        </div>
      </form>
    </BaseDrawer>
  )
}
